'use client';

import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { RadioStation, getStation } from '@/lib/radioStations';

export type RadioPlayState = 'idle' | 'loading' | 'playing' | 'paused' | 'error' | 'fallback';

export interface RadioContextValue {
  // Current state
  playState: RadioPlayState;
  selectedStationId: string | null;
  selectedStation: RadioStation | null;
  volume: number;
  isFallback: boolean;
  errorMessage: string | null;
  // Actions
  selectAndPlay: (stationId: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  setVolume: (v: number) => void;
  retryStream: () => void;
}

const RadioContext = createContext<RadioContextValue | null>(null);

export function useRadio(): RadioContextValue {
  const ctx = useContext(RadioContext);
  if (!ctx) throw new Error('useRadio must be used inside GlobalRadioProvider');
  return ctx;
}

const LOAD_TIMEOUT_MS = 5000; // 5 seconds

export function GlobalRadioProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [playState, setPlayState] = useState<RadioPlayState>('idle');
  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);
  const [volume, setVolumeState] = useState(70);
  const [isFallback, setIsFallback] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const selectedStation = selectedStationId ? (getStation(selectedStationId) ?? null) : null;

  // Initialize audio element once
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const audio = new Audio();
    audio.preload = 'none';
    audio.volume = 0.7;
    audioRef.current = audio;

    return () => {
      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };
  }, []);

  const clearLoadTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startFallback = useCallback((station: RadioStation) => {
    clearLoadTimeout();
    const audio = audioRef.current;
    if (!audio) return;

    setIsFallback(true);
    setPlayState('loading');

    audio.src = station.fallbackAudio;
    audio.load();

    const onCanPlay = () => {
      audio.play().then(() => {
        setPlayState('fallback');
        setErrorMessage(null);
      }).catch(() => {
        setPlayState('error');
        setErrorMessage('La radio ne peut pas démarrer pour le moment.');
      });
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('error', onFallbackError);
    };

    const onFallbackError = () => {
      setPlayState('error');
      setErrorMessage('La radio ne peut pas démarrer pour le moment.');
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('error', onFallbackError);
    };

    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('error', onFallbackError);
  }, [clearLoadTimeout]);

  const selectAndPlay = useCallback((stationId: string) => {
    const station = getStation(stationId);
    if (!station) return;

    const audio = audioRef.current;
    if (!audio) return;

    // Stop current playback
    clearLoadTimeout();
    audio.pause();
    audio.src = '';

    setSelectedStationId(stationId);
    setIsFallback(false);
    setErrorMessage(null);
    setPlayState('loading');

    const streamUrl = station.streamUrl;

    if (!streamUrl) {
      // No stream URL configured — go straight to fallback
      startFallback(station);
      return;
    }

    audio.src = streamUrl;
    audio.load();

    const onCanPlay = () => {
      clearLoadTimeout();
      audio.play().then(() => {
        setPlayState('playing');
      }).catch((err) => {
        // Autoplay blocked by browser
        if (err.name === 'NotAllowedError') {
          setPlayState('error');
          setErrorMessage('Le navigateur a bloqué la lecture. Appuyez sur « Démarrer RCI ».');
        } else {
          startFallback(station);
        }
      });
      cleanup();
    };

    const onError = () => {
      clearLoadTimeout();
      startFallback(station);
      cleanup();
    };

    const cleanup = () => {
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('error', onError);
    };

    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('error', onError);

    // Timeout fallback
    timeoutRef.current = setTimeout(() => {
      cleanup();
      startFallback(station);
    }, LOAD_TIMEOUT_MS);
  }, [clearLoadTimeout, startFallback]);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    setPlayState('paused');
  }, []);

  const resume = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.play().then(() => {
      setPlayState(isFallback ? 'fallback' : 'playing');
    }).catch(() => {
      setPlayState('error');
      setErrorMessage('La radio ne peut pas démarrer pour le moment.');
    });
  }, [isFallback]);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    clearLoadTimeout();
    audio.pause();
    audio.src = '';
    setPlayState('idle');
    setSelectedStationId(null);
    setIsFallback(false);
    setErrorMessage(null);
  }, [clearLoadTimeout]);

  const setVolume = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(100, v));
    setVolumeState(clamped);
    if (audioRef.current) {
      audioRef.current.volume = clamped / 100;
    }
  }, []);

  const retryStream = useCallback(() => {
    if (selectedStationId) {
      selectAndPlay(selectedStationId);
    }
  }, [selectedStationId, selectAndPlay]);

  const value: RadioContextValue = {
    playState,
    selectedStationId,
    selectedStation,
    volume,
    isFallback,
    errorMessage,
    selectAndPlay,
    pause,
    resume,
    stop,
    setVolume,
    retryStream,
  };

  return (
    <RadioContext.Provider value={value}>
      {children}
    </RadioContext.Provider>
  );
}
